<template>
  <div class="c-individual-list-page__top-bar">
    <div class="flex flex-col">
      <div class="flex justify-between">
        <div class="flex">
          <BittsAvatarStack
            size="medium"
            :org-list="orgsList"
            :transparent="false"
          />
          <div class="flex flex-col ml-20 self-center">
            <p
              class="font-bold text-xl"
              data-testid="c-individual-list-page__name"
            >
              {{ currentList?.name }}
            </p>
          </div>
        </div>
        <div class="flex items-start">
          <BittsUserStack
            class="mr-8"
            size="small"
            :users="allUsersInList"
            :include-hover-text="true"
          />
          <ListUserPermissionModule
            data-testid="c-individual-list-page__invite-btn"
            class="mr-8"
            :list-users="allUsersInList"
            :list-id="currentList?.id"
            :is-visible="permissionsModuleVisible"
            @invite-button-clicked="
              permissionsModuleVisible = !permissionsModuleVisible
            "
            @closed="permissionsModuleVisible = false"
          />
          <BittsButton
            :center-icon="['fak', 'edit']"
            aria-label="Edit list"
            type="neutral"
            size="small"
            variant="outline"
            @click="onEditListBtnClicked"
          />
        </div>
      </div>
      <div class="mt-16">
        <div class="flex flex-col">
          <p class="max-w-[800px]" data-testid="c-individual-list-page__desc">
            {{ currentList?.description }}
          </p>
          <div class="flex items-center mt-16">
            <p
              class="flex items-center text-neutral-text-weak text-sm mr-8"
              data-testid="c-individual-list-page__create-date"
            >
              <BittsAvatarPip
                size="small"
                class="mr-8"
                :is-user-main-avatar="true"
                :org="ownerOrg"
                :show-initials="true"
                :user="listOwner"
                :include-hover-text="true"
                :show-tooltip-org-info="false"
                hover-text-placement="bottomLeft"
              >
                <template #additionalInfo>
                  <p>{{ listOwner?.email }}</p>
                </template>
              </BittsAvatarPip>
              {{ formattedListCreatedInfo }}
            </p>
            <p
              class="flex items-center text-neutral-text-weak text-sm mr-8"
              data-testid="c-individual-list-page__last-updated"
            >
              <BittsAvatarPip
                size="small"
                class="mr-8"
                :is-user-main-avatar="true"
                :org="
                  userLastUpdatedList
                    ? userLastUpdatedList?.organization_id === currentOrg.id
                      ? currentOrg
                      : partnerOrg
                    : secondaryOrg
                "
                :show-initials="true"
                :user="userLastUpdatedList ? userLastUpdatedList : partnerOwner"
                :include-hover-text="true"
                :show-tooltip-org-info="false"
                :hover-text-placement="
                  userLastUpdatedList ? 'bottomLeft' : 'bottom'
                "
              >
                <template #additionalInfo>
                  <p>{{
                    userLastUpdatedList
                      ? userLastUpdatedList?.email
                      : partnerOwner?.email
                  }}</p>
                </template>
              </BittsAvatarPip>
              {{ formattedListUpdatedInfo }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  BittsAvatarPip,
  BittsAvatarStack,
  BittsButton,
  BittsUserStack,
} from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed, ref } from 'vue';

import ListUserPermissionModule from '@/components/shared-list/ListUserPermissionModule.vue';

import useAuth from '@/composables/useAuth';
import useSharedList from '@/composables/useSharedList';
import { useCollaborateStore } from '@/stores';

const emit = defineEmits<(e: 'open-modal', value: typeof EDIT_LIST) => void>();

const EDIT_LIST = 'edit_list';

const collaborateStore = useCollaborateStore();
const { currentOrg } = useAuth();

const { currentList, partnerOrg, allUsersInList, orgsList } = useSharedList();

const listOwner = computed(() =>
  collaborateStore.getOwnerByListId(currentList.value?.id),
);
const partnerOwner = computed(() =>
  collaborateStore.getPartnerOwnerByListId(currentList.value?.id),
);

const ownerOrg = computed(() =>
  collaborateStore.getOwnerOrgByListId(currentList.value?.id),
);
const secondaryOrg = computed(() =>
  collaborateStore.getPartnerOrgByListId(currentList.value?.id),
);

const formattedListCreatedInfo = computed(
  () =>
    `Created ${DateTime.fromISO(currentList.value?.created_at).toFormat('MMM d, yyyy')}`,
);
const formattedListUpdatedInfo = computed(
  () =>
    `Last updated ${DateTime.fromISO(currentList.value?.updated_at).toRelative()}`,
);

const userLastUpdatedList = computed(
  () => currentList.value?.last_updated_by_user,
);

const permissionsModuleVisible = ref(false);

function onEditListBtnClicked() {
  emit('open-modal', EDIT_LIST);
}
</script>
<style lang="pcss">
@reference "../../css/theme.css";
.c-individual-list-page__top-bar {
  @apply pb-24 px-24 border-b border-neutral-border;
}
</style>
